import type { ReactNode } from "react";
import {
  AwardIcon,
  DollarSignIcon,
  PhoneIcon,
  SendIcon,
  UserCheckIcon,
} from "lucide-react";

import { Column } from "@/components";
import {
  LeaderboardHouseCard,
  MostRevenueAndRacetrackContainer,
} from "@/components/leaderboard";
import {
  INTERVIEWS_GRADIENT,
  PLACEMENTS_GRADIENT,
  REVENUE_GRADIENT,
  SCREENS_GRADIENT,
  SUBMITTALS_GRADIENT,
} from "@/lib/constants";
import { formatting } from "@/lib/formatting";
import { cn } from "@/lib/utils";

import type { LeaderboardQueryParams, LeaderboardUser } from "@/types";

export const LeaderboardTopPerformerCards = ({
  users,
  queryParams,
  showRacetrack,
  controls,
}: {
  users: LeaderboardUser[];
  queryParams: LeaderboardQueryParams;
  showRacetrack: boolean;
  // View controls, rendered inside the racetrack card when it's shown.
  controls: ReactNode;
}) => {
  /* Render */
  const render = () => {
    return (
      <Column className="gap-6">
        {/* Padded card, not full viewport: the full-screen scene was the
            biggest repaint cost while scrolling on low-end laptops. */}
        {showRacetrack && (
          <Column className="mx-6 pt-6">
            <MostRevenueAndRacetrackContainer
              queryParams={queryParams}
              controls={controls}
            />
          </Column>
        )}
        <div
          className={cn(
            "mx-6 grid grid-cols-1 items-stretch gap-6 sm:grid-cols-2 xl:grid-cols-4",
            !showRacetrack && "pt-20 xl:grid-cols-5",
          )}
        >
          {!showRacetrack && (
            <LeaderboardHouseCard
              users={users}
              metricKey="revenue"
              label="Most Revenue"
              icon={DollarSignIcon}
              hexGradient={REVENUE_GRADIENT}
              formatValue={formatting.compactUsd}
            />
          )}
          <LeaderboardHouseCard
            users={users}
            metricKey="placements"
            label="Most Placements"
            icon={AwardIcon}
            hexGradient={PLACEMENTS_GRADIENT}
          />
          <LeaderboardHouseCard
            users={users}
            metricKey="screens"
            label="Most Screens"
            icon={PhoneIcon}
            hexGradient={SCREENS_GRADIENT}
          />
          <LeaderboardHouseCard
            users={users}
            metricKey="submittals"
            label="Most Submits"
            icon={SendIcon}
            hexGradient={SUBMITTALS_GRADIENT}
          />
          <LeaderboardHouseCard
            users={users}
            metricKey="interviews"
            label="Most Interviews"
            icon={UserCheckIcon}
            hexGradient={INTERVIEWS_GRADIENT}
          />
        </div>
      </Column>
    );
  };

  return render();
};
